// src/store/slices/categorySlice.ts
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../services/apiService';
import { toast } from 'react-toastify';

const api = apiService();

// ————————————————————————————————
// Async actions for Categories
// ————————————————————————————————

export const addCategory = createAsyncThunk(
  'categories/addCategory',
  async (categoryData: { name: string }) => {
    const response = await api.post('/category', categoryData);
    return response.data;
  }
);

export const fetchCategories = createAsyncThunk(
  'categories/fetchCategories',
  async () => {
    const response = await api.get('/category');
    return response.data;
  }
);

export const updateCategory = createAsyncThunk(
  'categories/updateCategory',
  async ({ categoryId, name }: { categoryId: string; name: string }) => {
    const response = await api.patch(`/category/${categoryId}`, { name });
    return response.data;
  }
);

// (Optional) If you support deleting categories, you could add:
// export const deleteCategory = createAsyncThunk(
//   'categories/deleteCategory',
//   async (categoryId: string) => {
//     await api.delete(`/category/${categoryId}`);
//     return categoryId;
//   }
// );

// ————————————————————————————————
// State and Slice
// ————————————————————————————————

interface Category {
  id: string;
  name: string;
  // add other fields if your API returns them
}

interface CategoryState {
  list: Category[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: CategoryState = {
  list: [],
  status: 'idle',
  error: null,
};

const categorySlice = createSlice({
  name: 'categories',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // addCategory
      .addCase(addCategory.fulfilled, (state, action) => {
        state.list.push(action.payload);
      })
      .addCase(addCategory.rejected, (state, action) => {
        toast.error('Failed to add category');
        state.error = action.error.message ?? null;
      })

      // fetchCategories
      .addCase(fetchCategories.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.list = action.payload;
        state.status = 'succeeded';
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? null;
      })

      // updateCategory
      .addCase(updateCategory.fulfilled, (state, action) => {
        const idx = state.list.findIndex((c) => c.id === action.payload.id);
        if (idx !== -1) state.list[idx] = action.payload;
        toast.success('Category updated successfully');
      })
      .addCase(updateCategory.rejected, (state, action) => {
        toast.error('Failed to update category');
        state.error = action.error.message ?? null;
      });

    // // deleteCategory
    // .addCase(deleteCategory.fulfilled, (state, action) => {
    //   state.list = state.list.filter((c) => c.id !== action.payload);
    // })
    // .addCase(deleteCategory.rejected, (state, action) => {
    //   toast.error('Failed to delete category');
    //   state.error = action.error.message ?? null;
    // });
  },
});

export default categorySlice.reducer;
